import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import AdminConfirmationDialog from '../../../../components/admin/AdminConfirmationDialog';
import AdminSurface from '../../../../components/admin/AdminSurface';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';
import type {
  StoreDetailsFeatureFlagClient,
  StoreDetailsFeatureFlagsTabProps,
} from '../../../../types/features/storeDetails/featureFlags';
import { useStoreDetailsFeatureFlags } from '../hooks/useStoreDetailsFeatureFlags';
import { STORE_DETAILS_FEATURE_FLAG_CLIENT_LABELS } from '../utils/storeDetailsFeatureFlagsDisplay';
import StoreDetailsFeatureFlagsTable from './StoreDetailsFeatureFlagsTable';

const StoreDetailsFeatureFlagsTab: React.FC<StoreDetailsFeatureFlagsTabProps> = ({ storeId }) => {
  const {
    activeClient,
    assignmentConfirmationContent,
    error,
    loading,
    mutatingFeatureId,
    mutationError,
    onAssignmentChange,
    onAssignmentConfirmationCancel,
    onAssignmentConfirmationConfirm,
    onClientChange,
    onRetry,
    rows,
  } = useStoreDetailsFeatureFlags(storeId);

  return (
    <AdminSurface sx={{ overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{
          alignItems: { xs: 'stretch', md: 'center' },
          borderBottom: `1px solid ${adminColors.divider}`,
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
            Feature Flags
          </Typography>
          <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText, mt: 0.25 }}>
            Manage selected-store assignments and review effective status for this store.
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeClient}
          onChange={(_event, nextClient: StoreDetailsFeatureFlagClient | null) => onClientChange(nextClient)}
          sx={{
            height: 36,
            '& .MuiToggleButton-root': {
              borderColor: adminColors.borderHover,
              color: adminColors.text,
              fontSize: adminTypography.button.fontSize,
              fontWeight: adminTypography.button.fontWeight,
              minWidth: 96,
              px: 1.5,
              textTransform: 'none',
            },
            '& .Mui-selected': {
              backgroundColor: `${adminColors.primary} !important`,
              color: `${adminColors.surface} !important`,
            },
          }}
        >
          <ToggleButton value="webapp">
            {STORE_DETAILS_FEATURE_FLAG_CLIENT_LABELS.webapp}
          </ToggleButton>
          <ToggleButton value="mobile_app">
            {STORE_DETAILS_FEATURE_FLAG_CLIENT_LABELS.mobile_app}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <StoreDetailsFeatureFlagsTable
        error={error}
        loading={loading}
        mutatingFeatureId={mutatingFeatureId}
        mutationError={mutationError}
        rows={rows}
        onAssignmentChange={onAssignmentChange}
        onRetry={onRetry}
      />

      {assignmentConfirmationContent ? (
        <AdminConfirmationDialog
          confirmButtonText={assignmentConfirmationContent.confirmButtonText}
          confirmTone={assignmentConfirmationContent.confirmTone}
          message={assignmentConfirmationContent.message}
          open
          submitting={Boolean(mutatingFeatureId)}
          title={assignmentConfirmationContent.title}
          onCancel={onAssignmentConfirmationCancel}
          onConfirm={onAssignmentConfirmationConfirm}
        />
      ) : null}
    </AdminSurface>
  );
};

export default React.memo(StoreDetailsFeatureFlagsTab);
